import { Component, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import {StringUtils} from '../../utils/string-utils';
import {Entity} from '../../entity/entity';
import {OrderService} from '../../services/order-service';
import {ReleaseLine} from './release-line';
import {ReleaseLineDetail} from './release-line-detail';
import {FormsModule} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';


@Component({
    selector: 'tag-release',
    templateUrl: './tag-release.component.html',
    styleUrls: ['./tag-release.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatButton, MatIcon, FormsModule]
})
export class TagReleaseComponent {
  private readonly orderService = inject(OrderService);


  private selectedDetailStart!: number;
  public readonly releaseLines = input.required<ReleaseLine[]>();
  public readonly handChangeAllowed = input<boolean>(true);
  public readonly releaseGenerated = output<Entity>();
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);
  public totalQuantity: number = 0;
  public selectedLine!: ReleaseLine;

  selectLine(line: ReleaseLine): void {
    this.selectedLine = line;
    if (!this.selectedLine.details) {
      this.resetStatus();
      this.orderService.getLineDetails(line).subscribe(results => {
        this.selectedLine.details = results.sort((first, second) => first.sequence - second.sequence);
        this.isBusy.set(false);
      }, error => this.handleError(error));
    }
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
  }

  selectDetail(clickEvent: any, detail: ReleaseLineDetail): void {
    if (clickEvent.which != 1) {
      return;
    }
    if (!clickEvent.ctrlKey && !clickEvent.shiftKey) {
      detail.selected = !detail.selected;
      this.selectedDetailStart = this.selectedLine.details.indexOf(detail);
    } else {
      if (clickEvent.ctrlKey) {
        detail.selected = !detail.selected;
        this.selectedDetailStart = this.selectedLine.details.indexOf(detail);
      }
      if (clickEvent.shiftKey) {
        let clickedIndex = this.selectedLine.details.indexOf(detail);
        let first = Math.min(clickedIndex, this.selectedDetailStart);
        let last = Math.max(clickedIndex, this.selectedDetailStart);
        for (let index = first; index <= last; index++) {
          this.selectedLine.details[index].selected = true;
        }
      }
    }
    this.updateQuantity();
  }

  updateQuantity(): void {
    let quantity: number = 0;
    this.releaseLines().forEach(line => {
      if (line.details && line.details.length > 0) {
        line.quantityToRelease = line.details.filter(detail => detail.selected).length;
        quantity += line.quantityToRelease;
      } else {
        line.quantityToRelease = 0;
      }
    });
    this.totalQuantity = quantity;
  }

  generateRelease(): void {
    this.resetStatus();
    let selectedLines: ReleaseLine[] = this.releaseLines().filter(line => line.quantityToRelease > 0);
    if (selectedLines.length === 0) {
      this.errorMessage.set("No lines were selected to release");
      this.isBusy.set(false);
    } else {
      this.orderService.releaseByTag(selectedLines).subscribe(result => {
        this.releaseGenerated.emit(result);
      }, error => this.handleError(error));
    }
  }

  allDetailsSelected(): boolean {
    let allSelected: boolean = false;
    if (this.selectedLine && this.selectedLine.details) {
      allSelected = this.selectedLine.details.length === this.selectedLine.quantityToRelease;
    }
    return allSelected;
  }

  toggleAllSelection(): void {
    let selectAll: boolean = !this.allDetailsSelected();
    this.selectedLine.details.forEach(detail => detail.selected = selectAll);
    this.updateQuantity();
  }

  changeAvailable(hand: string): boolean {
    let available: boolean = false;
    if (this.handChangeAllowed() && this.selectedLine && this.selectedLine.details) {
      let selectedDetails: ReleaseLineDetail[] = this.selectedLine.details.filter(detail => detail.selected);
      let currentSelections: ReleaseLineDetail[] = selectedDetails.filter(detail => detail.hand == hand);
      available = selectedDetails.length > currentSelections.length;
    }
    return available;
  }

  changeSelectedTagsHand(hand: string): void {
    if (this.handChangeAllowed() && this.selectedLine && this.selectedLine.details) {
      let selectedDetails: ReleaseLineDetail[] = this.selectedLine.details.filter(detail => detail.selected);
      selectedDetails.filter(detail => detail.hand != hand).forEach(detail => detail.hand = hand);
    }
  }

}

